import { useState } from 'react';

import {
  Anchor,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Heading,
  Icon,
  Label,
  LabeledControl,
  Modal,
  Paragraph,
  TextButton,
  Upsell,
} from '@zillow/constellation-10';
import { IconSparkleFilled } from '@zillow/constellation-icons';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserFeaturesBetaWebApiHandler_details,
  useRentalManagerUserFeaturesBetaWebApiHandler_enroll,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { setFeaturesBetaMembership } from '../../hooks/setFeaturesBetaMembership';
import { getCookieAsBoolean, setCookie } from '../../utils/cookieUtils';

import { BETA_PROMPT_COOKIES } from '../../constants/CookieConstants';
import { FEATURES_BETA_TAG } from '../../constants/invalidate';

import type { MouseEvent } from 'react';

// NOTE: zillow.com is a placeholder; point this at the terms page once ZRM-17056 lands (ZRM-17634)
const TERMS_HREF = 'https://www.zillow.com';

export type BetaFeatureOptInPromptProps = {
  featureName: string;
  variant?: 'banner' | 'modal';
};

const BetaFeatureOptInPrompt = ({ featureName, variant = 'banner' }: BetaFeatureOptInPromptProps) => {
  const dismissedCookieName = `${BETA_PROMPT_COOKIES.DISMISSED_PREFIX}${featureName}`;
  const [isDismissed, setIsDismissed] = useState(() => getCookieAsBoolean(dismissedCookieName));
  const [hasOpenedTerms, setHasOpenedTerms] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const queryClient = useQueryClient();

  const { data } = useRentalManagerUserFeaturesBetaWebApiHandler_details(
    { featureName },
    {
      enabled: !isDismissed,
      meta: {
        tags: [FEATURES_BETA_TAG],
        context: { api: 'featuresBetaDetails', details: { featureName } },
      },
    },
  );

  const {
    mutate: enroll,
    isPending: isEnrolling,
    isSuccess: isEnrolled,
    isError: hasEnrollError,
  } = useRentalManagerUserFeaturesBetaWebApiHandler_enroll({
    meta: {
      context: { api: 'featuresBetaEnroll', details: { featureName } },
    },
    onSuccess: () => setFeaturesBetaMembership(queryClient, true),
  });

  const title = data?.optInPrompt?.title;
  const body = data?.optInPrompt?.body;

  if (isDismissed || isEnrolled || !data?.isFeatureEnabled || !data.canSelfEnroll || !body) {
    return null;
  }

  const canJoin = hasAcceptedTerms && !isEnrolling;

  const handleDismiss = () => {
    setCookie(dismissedCookieName, 'true', BETA_PROMPT_COOKIES.EXPIRATION);
    setIsDismissed(true);
  };

  // The link sits inside the checkbox label, so the tab is opened here; letting the click through toggles the checkbox
  const handleOpenTerms = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    window.open(TERMS_HREF, '_blank', 'noopener,noreferrer');
    setHasOpenedTerms(true);
  };

  const renderHeading = (text: string) => (
    <Heading level={2} textStyle="heading-xs">
      {text}
    </Heading>
  );

  const content = (
    <>
      <Paragraph>{body}</Paragraph>

      <Box css={{ marginBlockStart: '100' }}>
        <LabeledControl
          controlId={`BetaFeatureOptInPrompt-terms-${featureName}`}
          label={
            <Label>
              I’ve read the{' '}
              <Anchor href={TERMS_HREF} target="_blank" rel="noopener noreferrer" onClick={handleOpenTerms}>
                Terms of Service
              </Anchor>
            </Label>
          }
          control={
            <Checkbox
              checked={hasAcceptedTerms}
              disabled={!hasOpenedTerms}
              onChange={(event) => setHasAcceptedTerms(event.target.checked)}
            />
          }
        />
      </Box>

      {hasEnrollError && (
        <Box role="alert" css={{ marginBlockStart: '100', color: 'text.feedback.critical.strong' }}>
          We couldn’t add you to the beta. Try joining again.
        </Box>
      )}
    </>
  );

  if (variant === 'modal') {
    return (
      <Modal
        open
        onOpenChange={(open) => !open && handleDismiss()}
        size={{ base: 'fullScreen', md: 'md' }}
        dividers={false}
        header={
          <Box css={{ display: 'flex', alignItems: 'center', gap: '200' }}>
            <Icon render={<IconSparkleFilled />} />
            {renderHeading(title ?? 'Join the beta')}
          </Box>
        }
        body={content}
        footer={
          <ButtonGroup aria-label="Join the beta or dismiss">
            <Button type="button" tone="brand" emphasis="bare" onClick={handleDismiss}>
              Not now
            </Button>
            <Button type="button" tone="brand" emphasis="filled" onClick={() => enroll()} disabled={!canJoin}>
              Join the beta
            </Button>
          </ButtonGroup>
        }
      />
    );
  }

  return (
    <Upsell.Root tone="soft" elevated={false} css={{ marginBottom: '400' }} onOpenChange={handleDismiss}>
      <Upsell.Media>
        <Icon render={<IconSparkleFilled />} />
      </Upsell.Media>

      <Upsell.Content>
        {title && <Upsell.Header>{renderHeading(title)}</Upsell.Header>}

        <Upsell.Body>{content}</Upsell.Body>

        <Upsell.Action>
          <TextButton type="button" onClick={() => enroll()} disabled={!canJoin}>
            Join the beta
          </TextButton>
        </Upsell.Action>
      </Upsell.Content>

      <Upsell.CloseButton />
    </Upsell.Root>
  );
};

export default BetaFeatureOptInPrompt;
